import React, { useState, useEffect, useRef } from "react";
import axios from 'axios';
import dayjs from 'dayjs';
import "../../css/dashboard.css";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import DaumPostcode from 'react-daum-postcode';

import CommonTable from "../component/table_list/common_table";
import CommonTableRow from "../component/table_list/common_tablerow";
import CommonTableColumn from "../component/table_list/common_tablecolumn";
import { formatWon } from '../utils/format_amount';

function NoticeListDetail() {
    const location = useLocation();
    const navigate = useNavigate();
    const { idx } = location.state;

    const [reserve, setReserve] = useState(null);
    const [loading, setLoading] = useState(true);

    // Edit Modal State
    const modalEdit = useRef();
    const addressModalRef = useRef();
    const [addressModalOpen, setAddressModalOpen] = useState(false);
    const [productList, setProductList] = useState([]);

    // Manager Match Modal State
    const [managerList, setManagerList] = useState([]);
    const [page, setPage] = useState(1);
    const items = 10;
    const modalMatch = useRef();

    // Notice Info State (for Edit)
    const [noticeInfo, setNoticeInfo] = useState({
        idx: '',
        req_number: '',
        work_date: '',
        work_time: '',
        address: '',
        address_sub: '',
        info: '',
        option: '',
        working_kind: '',
        amount: '',
        memo: '',
        admin_memo: ''
    });
    const [selectedProducts, setSelectedProducts] = useState([]);

    useEffect(() => {
        getReserveDetail();
        // Fetch Product List for Modal
        const getProductList = async () => {
            try {
                const res = await axios.get('/PriceInfo');
                if (res.data.success) {
                    setProductList(res.data.message);
                }
            } catch (err) {
                console.error("Error fetching product list:", err);
            }
        };
        getProductList();
    }, []);

    // Address Modal Control
    useEffect(() => {
        if (addressModalOpen) {
            addressModalRef.current?.showModal();
        } else {
            addressModalRef.current?.close();
        }
    }, [addressModalOpen]);

    const getReserveDetail = async () => {
        try {
            const res = await axios.get(`/NoticeList/Detail/${idx}`);
            if (res.data.success && res.data.message.length > 0) {
                setReserve(res.data.message[0]);
            }
        } catch (error) {
            console.error('Error fetching detail:', error);
        } finally {
            setLoading(false);
        }
    };

    const getManagerList = async () => {
        try {
            const res = await axios.get('/ManagerRegist'); // Using existing API
            if (res.data.success) {
                setManagerList(res.data.message);
            }
        } catch (error) { console.error(error); }
    };

    // Modal Handlers
    const startEditModal = () => {
        if (!reserve) return;

        // Parse info string to selectedProducts
        const parsedProducts = [];
        if (reserve.info) {
            const items = reserve.info.split(',').map(item => item.trim());
            items.forEach(item => {
                const lastSpaceIndex = item.lastIndexOf(' ');
                let name = item;
                let count = 1;

                if (lastSpaceIndex > -1) {
                    name = item.substring(0, lastSpaceIndex);
                    const countStr = item.substring(lastSpaceIndex + 1);
                    // Extract number from "3대"
                    const countMatch = countStr.match(/\d+/);
                    if (countMatch) {
                        count = parseInt(countMatch[0], 10);
                    }
                }
                parsedProducts.push({ name, count });
            });
        }

        setNoticeInfo({
            idx: reserve.idx,
            req_number: reserve.req_number || '',
            work_date: reserve.work_date || '',
            work_time: reserve.work_time || '',
            address: reserve.address || '',
            address_sub: reserve.address_sub || '',
            info: reserve.info || '',
            option: reserve.option || '',
            working_kind: reserve.working_kind || '',
            amount: reserve.amount || '',
            memo: reserve.memo || '',
            admin_memo: reserve.admin_memo || ''
        });
        setSelectedProducts(parsedProducts);
        modalEdit.current.showModal();
    }

    const closeEditModal = () => {
        modalEdit.current.close();
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'amount') {
            if (value.length > 10 || Number(value) > 2000000000) return;
        }
        setNoticeInfo(prev => ({ ...prev, [name]: value }));
    }

    const handleAddressComplete = (data) => {
        let fullAddress = data.address;
        let extraAddress = '';

        if (data.addressType === 'R') {
            if (data.bname !== '') extraAddress += data.bname;
            if (data.buildingName !== '') extraAddress += (extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName);
            fullAddress += (extraAddress !== '' ? ` (${extraAddress})` : '');
        }

        setNoticeInfo(prev => ({ ...prev, address: fullAddress }));
        setAddressModalOpen(false);
    };

    const handleAddInfo = (e) => {
        const selectedItem = e.target.value;
        if (!selectedItem) return;
        if (!selectedProducts.find(p => p.name === selectedItem)) {
            setSelectedProducts(prev => [...prev, { name: selectedItem, count: 1 }]);
        }
    };

    const handleRemoveInfo = (nameToRemove) => {
        setSelectedProducts(prev => prev.filter(p => p.name !== nameToRemove));
    };

    const handleCountChange = (name, count) => {
        const newCount = parseInt(count, 10);
        if (newCount < 1) return;
        setSelectedProducts(prev => prev.map(p => p.name === name ? { ...p, count: newCount } : p));
    };

    const handleUpdate = async () => {
        const infoString = selectedProducts.map(p => `${p.name} ${p.count}대`).join(', ');
        const payload = { ...noticeInfo, info: infoString };

        if (!payload.req_number || !payload.address) {
            alert('오더번호와 주소는 필수입니다.');
            return;
        }

        try {
            const res = await axios.put('/NoticeList/Update', payload);
            if (res.data.success) {
                alert('수정되었습니다.');
                closeEditModal();
                getReserveDetail();
            } else {
                alert('수정에 실패했습니다.');
            }
        } catch (error) {
            console.error('Update error:', error);
            alert('오류가 발생했습니다.');
        }
    };

    const openMatchModal = () => {
        // 매칭 상태인 경우 경고 메시지 표시
        if (reserve.status === '매칭') {
            if (!window.confirm('이미 매니저가 매칭된 공고건입니다.\n변경하시겠습니까?')) return;
        }
        getManagerList();
        modalMatch.current.showModal();
    };

    const handleMatch = async (manager) => {
        if (!window.confirm(`${manager.name} 매니저님을 배정하시겠습니까?`)) return;

        try {
            const res = await axios.put('/NoticeList/Match', {
                idx: reserve.idx,
                manager_id: manager.email, // Assuming email is ID as per table_detail.js
                manager_name: manager.name
            });
            if (res.data.success) {
                alert('매칭되었습니다.');
                modalMatch.current.close();
                getReserveDetail();
            } else {
                alert('매칭 실패');
            }
        } catch (error) {
            console.error(error);
            alert('오류 발생');
        }
    };

    const handleComplete = async () => {
        if (!window.confirm('공고를 완료 처리하시겠습니까?')) return;

        try {
            const res = await axios.put(`/NoticeList/Status/${reserve.idx}`, { status: '완료' });
            if (res.data.success) {
                alert('완료 처리되었습니다.');
                getReserveDetail();
            } else {
                alert('완료 처리에 실패했습니다.');
            }
        } catch (error) {
            console.error('Complete error:', error);
            alert('오류가 발생했습니다.');
        }
    };

    const handlePageChange = (page) => { setPage(page); };


    if (loading) return <div>Loading...</div>;
    if (!reserve) return <div>데이터를 찾을 수 없습니다.</div>;

    return (
        <PageContainer>
            <Card>
                <CardHeader>
                    <Title>공고 상세 정보</Title>
                    {(() => {
                        const isExpired = reserve.status === '공고' && 
                            reserve.work_date && 
                            reserve.work_time &&
                            dayjs(`${reserve.work_date} ${reserve.work_time}`).isBefore(dayjs());
                        const displayStatus = isExpired ? '마감' : (reserve.status || '대기');
                        return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {isExpired && (
                                    <span style={{ 
                                        color: '#d32f2f', 
                                        fontSize: '0.9rem', 
                                        fontWeight: '500',
                                        backgroundColor: '#ffebee',
                                        padding: '6px 12px',
                                        borderRadius: '6px'
                                    }}>
                                        ⚠️ 작업기한 내 매칭에 실패하였습니다
                                    </span>
                                )}
                                <Badge status={displayStatus}>{displayStatus}</Badge>
                            </div>
                        );
                    })()}
                </CardHeader>

                <SectionTitle>기본 정보</SectionTitle>
                <GridContainer>
                    <InfoItem label="오더번호" value={reserve.req_number} />
                    <InfoItem label="작업일자" value={reserve.work_date} />
                    <InfoItem label="작업시간" value={reserve.work_time} />
                    <InfoItem label="주소" value={`${reserve.address || ''} ${reserve.address_sub || ''}`} />
                    <InfoItem label="예상금액" value={formatWon(reserve.amount)} isHighlight />
                    <InfoItem label="상태" value={reserve.status} />
                    <InfoItem label="매니저 ID" value={reserve.manager_id} />
                    <InfoItem label="매니저 이름" value={reserve.manager_name} />
                </GridContainer>

                <SectionTitle>작업 정보</SectionTitle>
                <GridContainer>
                    <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <Label>예약상품</Label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {reserve.info ? reserve.info.split(',').map((item, idx) => {
                                const trimmed = item.trim();
                                const lastSpaceIndex = trimmed.lastIndexOf(' ');
                                const name = lastSpaceIndex > -1 ? trimmed.substring(0, lastSpaceIndex) : trimmed;
                                const count = lastSpaceIndex > -1 ? trimmed.substring(lastSpaceIndex + 1) : '';

                                return (
                                    <Badge key={idx} status={0} style={{ backgroundColor: '#f5f5f5', color: '#333', border: '1px solid #ddd', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span>{name}</span>
                                        {count && <span style={{ fontWeight: '800', color: '#006edc', backgroundColor: '#e3f2fd', padding: '2px 6px', borderRadius: '12px', fontSize: '0.9em' }}>{count}</span>}
                                    </Badge>
                                );
                            }) : '-'}
                        </div>
                    </div>
                    <InfoItem label="모집분야" value={reserve.working_kind} />
                    <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <Label>특이사항 (기사용)</Label>
                        <div style={{ padding: '10px', backgroundColor: '#fff', border: '1px solid #eee', borderRadius: '6px', minHeight: '60px', whiteSpace: 'pre-wrap' }}>
                            {reserve.memo || '-'}
                        </div>
                    </div>
                    <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <Label style={{ color: '#856404' }}>특이사항 (관리자 전용)</Label>
                        <div style={{ padding: '10px', backgroundColor: '#fffef5', border: '1px solid #ffc107', borderRadius: '6px', minHeight: '60px', whiteSpace: 'pre-wrap' }}>
                            {reserve.admin_memo || '-'}
                        </div>
                    </div>
                </GridContainer>

                <ButtonGroup>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {['대기', '매칭'].includes(reserve.status) && (
                            <PrimaryButton onClick={openMatchModal} style={{ backgroundColor: '#28a745' }}>매니저 매칭</PrimaryButton>
                        )}
                        {reserve.status === '매칭' && (
                            <OutlineButton onClick={handleComplete} style={{ marginLeft: '8px', borderColor: '#6c757d', color: '#6c757d' }}>완료</OutlineButton>
                        )}
                    </div>
                    <div style={{ flex: 1 }}></div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <PrimaryButton onClick={startEditModal}>수정</PrimaryButton>
                        <DarkButton onClick={() => navigate(-1)}>목록으로</DarkButton>
                    </div>
                </ButtonGroup>
            </Card>

            {/* Manager Match Modal */}
            <StyledDialog ref={modalMatch} style={{ width: '900px' }}>
                <DialogHeader>
                    <h3>매니저 매칭</h3>
                    <CloseButton onClick={() => modalMatch.current.close()}>&times;</CloseButton>
                </DialogHeader>
                <DialogContent>
                    <CommonTable headerTitle={['No', '아이디', '이름', '전화번호', '가입일자', '선택']}>
                        {managerList.slice(items * (page - 1), items * (page - 1) + items).map((manager, index) => (
                            <CommonTableRow key={index} kind="notDetail">
                                <CommonTableColumn>{((page - 1) * items) + index + 1}</CommonTableColumn>
                                <CommonTableColumn>{manager.email}</CommonTableColumn>
                                <CommonTableColumn>{manager.name}</CommonTableColumn>
                                <CommonTableColumn>{manager.phone_num}</CommonTableColumn>
                                <CommonTableColumn>{manager.regist_date}</CommonTableColumn>
                                <CommonTableColumn>
                                    <PrimaryButton onClick={() => handleMatch(manager)} style={{ fontSize: '0.8rem', padding: '4px 10px' }}>선택</PrimaryButton>
                                </CommonTableColumn>
                            </CommonTableRow>
                        ))}
                    </CommonTable>
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                        {/* Simple Pagination or import Pagination component if available */}
                    </div>
                </DialogContent>
            </StyledDialog>

            {/* Edit Modal */}
            <StyledDialog ref={modalEdit} style={{ width: '800px' }}>
                <DialogHeader>
                    <h3>{reserve.status === '대기' ? '공고 수정' : '관리자 메모 수정'}</h3>
                    <CloseButton onClick={closeEditModal}>&times;</CloseButton>
                </DialogHeader>
                <DialogContent>
                    {reserve.status !== '대기' && (
                        <div style={{ 
                            padding: '12px 16px', 
                            backgroundColor: '#fff3cd', 
                            border: '1px solid #ffc107', 
                            borderRadius: '6px', 
                            marginBottom: '20px',
                            color: '#856404'
                        }}>
                            ⚠️ 현재 상태({reserve.status})에서는 <strong>관리자 전용 특이사항</strong>만 수정할 수 있습니다.
                        </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <InputGroup>
                            <Label>오더번호</Label>
                            <StyledInput name="req_number" value={noticeInfo.req_number} onChange={handleChange} placeholder="예: R231212G1" disabled={reserve.status !== '대기'} />
                        </InputGroup>
                        <InputGroup>
                            <Label>예상금액</Label>
                            <StyledInput name="amount" type="number" value={noticeInfo.amount} onChange={handleChange} placeholder="숫자만 입력" disabled={reserve.status !== '대기'} />
                        </InputGroup>
                    </div>

                    <InputGroup>
                        <Label>주소</Label>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <StyledInput name="address" value={noticeInfo.address} onChange={handleChange} placeholder="주소 입력" disabled={reserve.status !== '대기'} />
                            <OutlineButton onClick={() => setAddressModalOpen(true)} type="button" style={{ whiteSpace: 'nowrap' }} disabled={reserve.status !== '대기'}>주소 검색</OutlineButton>
                        </div>
                    </InputGroup>

                    <InputGroup>
                        <Label>상세주소</Label>
                        <StyledInput name="address_sub" value={noticeInfo.address_sub} onChange={handleChange} placeholder="상세주소 입력 (동/호수 등)" disabled={reserve.status !== '대기'} />
                    </InputGroup>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <InputGroup>
                            <Label>작업일자</Label>
                            <StyledInput name="work_date" type="date" value={noticeInfo.work_date} onChange={handleChange} disabled={reserve.status !== '대기'} />
                        </InputGroup>
                        <InputGroup>
                            <Label>작업시간</Label>
                            <StyledInput name="work_time" type="time" value={noticeInfo.work_time} onChange={handleChange} disabled={reserve.status !== '대기'} />
                        </InputGroup>
                    </div>

                    <InputGroup>
                        <Label>예약상품 (정보)</Label>
                        <StyledSelect onChange={handleAddInfo} value="" disabled={reserve.status !== '대기'}>
                            <option value="">상품 선택</option>
                            {productList.map((product, idx) => (
                                <option key={idx} value={product.kind}>{product.kind}</option>
                            ))}
                        </StyledSelect>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                            {selectedProducts.map((item, idx) => (
                                <Badge key={idx} status={0} style={{ display: 'flex', alignItems: 'center', gap: '5px', paddingRight: '6px' }}>
                                    {item.name}
                                    <input
                                        type="number"
                                        value={item.count}
                                        onChange={(e) => handleCountChange(item.name, e.target.value)}
                                        style={{ width: '40px', padding: '2px', border: '1px solid #ccc', borderRadius: '4px', textAlign: 'center' }}
                                        min="1"
                                        disabled={reserve.status !== '대기'}
                                    />
                                    {reserve.status === '대기' && (
                                        <span
                                            onClick={() => handleRemoveInfo(item.name)}
                                            style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '1.1em', lineHeight: 0.8, marginLeft: '4px' }}
                                        >
                                            &times;
                                        </span>
                                    )}
                                </Badge>
                            ))}
                        </div>
                    </InputGroup>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <InputGroup>
                            <Label>모집분야</Label>
                            <StyledSelect name="working_kind" value={noticeInfo.working_kind} onChange={handleChange} disabled={reserve.status !== '대기'}>
                                <option value="">선택하세요</option>
                                <option value="감리">감리</option>
                                <option value="분해조립">분해조립</option>
                                <option value="핀세척">핀세척</option>
                                <option value="부품세척">부품세척</option>
                                <option value="딜리버리">딜리버리</option>
                            </StyledSelect>
                        </InputGroup>
                    </div>

                    <InputGroup>
                        <Label>특이사항 (기사용)</Label>
                        <textarea
                            name="memo"
                            value={noticeInfo.memo}
                            onChange={handleChange}
                            placeholder="기사 앱에서 보이는 특이사항"
                            disabled={reserve.status !== '대기'}
                            style={{
                                width: '100%',
                                padding: '0.6rem',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                fontSize: '0.95rem',
                                boxSizing: 'border-box',
                                minHeight: '80px',
                                resize: 'vertical',
                                fontFamily: 'inherit',
                                backgroundColor: reserve.status !== '대기' ? '#f5f5f5' : '#fff'
                            }}
                        />
                    </InputGroup>
                    <InputGroup>
                        <Label style={{ color: '#856404' }}>특이사항 (관리자 전용)</Label>
                        <textarea
                            name="admin_memo"
                            value={noticeInfo.admin_memo}
                            onChange={handleChange}
                            placeholder="관리자만 볼 수 있는 메모"
                            style={{
                                width: '100%',
                                padding: '0.6rem',
                                border: '1px solid #ffc107',
                                borderRadius: '6px',
                                fontSize: '0.95rem',
                                boxSizing: 'border-box',
                                minHeight: '80px',
                                resize: 'vertical',
                                fontFamily: 'inherit',
                                backgroundColor: '#fffef5'
                            }}
                        />
                    </InputGroup>
                </DialogContent>
                <DialogActions>
                    <OutlineButton onClick={closeEditModal}>취소</OutlineButton>
                    <PrimaryButton onClick={handleUpdate}>수정 완료</PrimaryButton>
                </DialogActions>
            </StyledDialog>

            {/* Address Search Modal */}
            <StyledDialog ref={addressModalRef} style={{ width: '500px' }}>
                <DialogHeader>
                    <h3>주소 검색</h3>
                    <CloseButton onClick={() => setAddressModalOpen(false)}>&times;</CloseButton>
                </DialogHeader>
                <DialogContent>
                    {addressModalOpen && <DaumPostcode onComplete={handleAddressComplete} />}
                </DialogContent>
            </StyledDialog>
        </PageContainer>
    );
}

// Reuse styled components from table_detail.js
const PageContainer = styled.div`
    padding: 30px 20px;
    background-color: #f5f6f8;
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: flex-start; 
    padding-top: 50px; 
    box-sizing: border-box;
`;

const Card = styled.div`
    background: white;
    width: 100%;
    max-width: 1000px;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    padding: 40px 45px;
    display: flex;
    flex-direction: column;
    gap: 25px;
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #eee;
    padding-bottom: 15px;
    margin-bottom: 10px;
`;

const Title = styled.h2`
    font-size: 1.6rem;
    color: #111;
    margin: 0;
    font-weight: 800;
`;

const Badge = styled.span`
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;
    background-color: ${props => {
        switch (props.status) {
            case '공고': return '#e3f2fd';  // 파란색 배경
            case '매칭': return '#f3e5f5';  // 보라색 배경
            case '완료': return '#e8f5e9';  // 녹색 배경
            case '마감': return '#f5f5f5';  // 회색 배경
            default: return '#fff3e0';       // 대기 - 주황색 배경
        }
    }};
    color: ${props => {
        switch (props.status) {
            case '공고': return '#1976d2';  // 파란색 글자
            case '매칭': return '#7b1fa2';  // 보라색 글자
            case '완료': return '#388e3c';  // 녹색 글자
            case '마감': return '#9e9e9e';  // 회색 글자
            default: return '#f57c00';       // 대기 - 주황색 글자
        }
    }};
`;

const SectionTitle = styled.h3`
    font-size: 1.15rem;
    color: #333;
    margin: 0 0 12px 0;
    border-left: 4px solid #006edc;
    padding-left: 12px;
`;

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    column-gap: 25px;
    row-gap: 20px;
    
    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const Label = styled.div`
    font-size: 0.85rem;
    color: #888;
    font-weight: 500;
    margin-bottom: 4px;
`;

const Value = styled.div`
    font-size: 1rem;
    color: ${props => props.isHighlight ? '#d32f2f' : '#222'};
    font-weight: ${props => props.isHighlight ? 'bold' : 'normal'};
    border-bottom: 1px solid #f0f0f0;
    padding-bottom: 6px;
    min-height: 24px;
    word-break: break-word;
`;

const ButtonGroup = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 25px;
    padding-top: 25px;
    border-top: 1px solid #eee;
`;

const BaseButton = styled.button`
    padding: 10px 22px;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    font-size: 0.95rem;
`;

const OutlineButton = styled(BaseButton)`
    background-color: white;
    border: 1px solid #ccc;
    color: #555;
    &:hover { background-color: #f9f9f9; border-color: #bbb; }
`;

const InfoItem = ({ label, value, span = 1, isHighlight }) => (
    <div style={{
        gridColumn: `span ${span}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '2px'
    }}>
        <Label>{label}</Label>
        <Value isHighlight={isHighlight}>{value || '-'}</Value>
    </div>
);

const PrimaryButton = styled(BaseButton)`
    background-color: #007bff;
    color: white;
    &:hover { background-color: #0056b3; }
`;

const DarkButton = styled(BaseButton)`
    background-color: #555;
    color: white;
    &:hover { background-color: #333; }
`;

const StyledDialog = styled.dialog`
    border: none; 
    border-radius: 16px; 
    padding: 0;
    box-shadow: 0 10px 40px rgba(0,0,0,0.25);
    &::backdrop { background: rgba(0, 0, 0, 0.6); }
`;

const DialogHeader = styled.div`
    padding: 16px 20px; 
    border-bottom: 1px solid #eee;
    display: flex; 
    justify-content: space-between; 
    align-items: center;
    h3 { margin: 0; font-size: 1.2rem; font-weight: 700; }
`;

const DialogContent = styled.div`
    padding: 24px; 
    background: #fff; 
    max-height: 80vh; 
    overflow-y: auto;
    display: flex; 
    flex-direction: column;
`;

const DialogActions = styled.div`
    padding: 16px 20px; 
    border-top: 1px solid #eee;
    display: flex; 
    justify-content: flex-end; 
    gap: 10px;
`;

const CloseButton = styled.button`
    background: none; 
    border: none; 
    font-size: 1.8rem; 
    cursor: pointer; 
    color: #999; 
    line-height: 1;
    &:hover { color: #333; }
`;

const InputGroup = styled.div`
    margin-bottom: 1rem;
`;

const StyledInput = styled.input`
    width: 100%;
    padding: 0.6rem;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.95rem;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
    }
`;

const StyledSelect = styled.select`
    width: 100%;
    padding: 0.6rem;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.95rem;
    box-sizing: border-box;
    background-color: white;

    &:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
    }
`;

export default NoticeListDetail;
